import { Injectable, Signal, computed, signal } from '@angular/core';

function buildKey(idMarca: string, idSubmarca: string): string {
  return `${idMarca}::${idSubmarca}`;
}

@Injectable({ providedIn: 'root' })
export class WsSyncState {
  private readonly syncMap = signal<Record<string, boolean>>({});
  private readonly pendingKeys = signal<Set<string>>(new Set());
  private readonly _showSyncAlert = signal<boolean>(false);
  private readonly _activeListLoaded = signal<boolean>(false);

  readonly hasAnyPending = computed(() => this.pendingKeys().size > 0);
  readonly showSyncAlert = this._showSyncAlert.asReadonly();
  readonly activeListLoaded = this._activeListLoaded.asReadonly();

  isSyncActive(idMarca: string, idSubmarca: string): Signal<boolean> {
    return computed(() => {
      if (!idMarca || !idSubmarca) return false;
      return this.syncMap()[buildKey(idMarca, idSubmarca)] === true;
    });
  }

  isPending(idMarca: string, idSubmarca: string): Signal<boolean> {
    return computed(() => {
      if (!idMarca || !idSubmarca) return false;
      return this.pendingKeys().has(buildKey(idMarca, idSubmarca));
    });
  }

  getSync(idMarca: string, idSubmarca: string): boolean {
    if (!idMarca || !idSubmarca) return false;
    return this.syncMap()[buildKey(idMarca, idSubmarca)] === true;
  }

  setSync(idMarca: string, idSubmarca: string, active: boolean): void {
    if (!idMarca || !idSubmarca) return;
    const key = buildKey(idMarca, idSubmarca);
    this.syncMap.update(current => ({ ...current, [key]: active }));
  }

  hydrateActive(items: { idMarca: string; idSubmarca: string; sincronizacionActiva: boolean }[]): void {
    this.syncMap.update(current => {
      const next = { ...current };
      for (const item of items) {
        if (!item.idMarca || !item.idSubmarca) continue;
        next[buildKey(item.idMarca, item.idSubmarca)] = item.sincronizacionActiva;
      }
      return next;
    });
    this._activeListLoaded.set(true);
  }

  markPending(idMarca: string, idSubmarca: string): void {
    if (!idMarca || !idSubmarca) return;
    const key = buildKey(idMarca, idSubmarca);
    this.pendingKeys.update(current => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }

  notifySyncStarted(): void {
    this._showSyncAlert.set(true);
  }

  dismissSyncAlert(): void {
    this._showSyncAlert.set(false);
  }

  clearPending(idMarca: string, idSubmarca: string): void {
    if (!idMarca || !idSubmarca) return;
    const key = buildKey(idMarca, idSubmarca);
    this.pendingKeys.update(current => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }
}
